import { useEffect } from 'react';
import { useRouteError, isRouteErrorResponse } from 'react-router';
import { datadogRum } from '@datadog/browser-rum';
import {
  Button,
  Typography,
  Flex,
  HomeFilled,
  ReloadOutlined,
  Text as PageText,
} from '@/uicomponents';
import { PageTitle } from '@/lib/hooks';
import { canRecoverFromStaleChunk, isStaleChunkError, recoverFromStaleChunk } from '@/lib/utils';
import { Show } from '@/components/shared';
import styles from './error-fallback.module.css';

const { Text } = Typography;

export function ErrorFallback() {
  const error = useRouteError();

  const is404 = isRouteErrorResponse(error) && error.status === 404;

  // NPJB-616 — a lazy-route chunk that vanished in a deploy is not an app error
  // and cannot be retried in place; re-fetching index.html picks up the new
  // chunk graph. Read the budget during render so we can skip painting the 500
  // page for a frame before the reload lands.
  const isStaleChunk = isStaleChunkError(error);
  const willReload = isStaleChunk && canRecoverFromStaleChunk();

  useEffect(() => {
    const recovering = isStaleChunk && recoverFromStaleChunk();

    if (error instanceof Error) {
      datadogRum.addError(error, {
        source: 'error-boundary',
        staleChunk: isStaleChunk,
        recovering,
      });
    }
  }, [error, isStaleChunk]);

  if (willReload) return null;

  const errorCode = is404 ? '404' : '500';
  const title = is404 ? 'Page Not Found' : 'Something went wrong';
  const subtitle = is404
    ? "The page you're looking for doesn't exist or has been moved. Check the URL or head back home."
    : 'An unexpected error occurred. The Digitalzone team has been notified. Please try again or return home.';

  const pageTitle = is404 ? 'Page Not Found' : 'Error';

  return (
    <>
      <PageTitle title={pageTitle} />
      <Flex align='center' justify='center' className={styles.page}>
        <Flex vertical align='center' gap={24} className={styles.card}>
          <Text className={styles.errorCode}>{errorCode}</Text>

          <Flex className={styles.divider}>{' '}</Flex>

          <Flex vertical align='center' gap={8}>
            {/* U30 — the same 28px page title the 404 page uses. */}
            <PageText variant='pageTitle' headingLevel={1}>
              {title}
            </PageText>
            <Text className={styles.subtitle}>{subtitle}</Text>
          </Flex>

          <Flex gap={12} wrap justify='center'>
            <Button
              type='primary'
              icon={<HomeFilled />}
              onClick={() => {
                globalThis.location.href = '/';
              }}
            >
              Back to Home
            </Button>
            <Show when={!is404}>
              <Button icon={<ReloadOutlined />} onClick={() => globalThis.location.reload()}>
                Reload Page
              </Button>
            </Show>
          </Flex>
        </Flex>
      </Flex>
    </>
  );
}
